// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Data hooks for the alarm-operations surfaces. These follow the usePlans
// pattern ({ data, loading, error, refetch }, cancelled guard) so swapping the
// mock service for a real one later is transparent to screens.

import { useState, useEffect, useCallback } from 'react';
import type {
  Alarm,
  AlarmBucket,
  AlarmQuery,
  AuditEntry,
  FloorPlan,
  LogNote,
  MaintenanceTask,
  Routine,
} from '../types/alarm';
import * as alarmsService from '../services/alarmsService';
import * as opsDataService from '../services/opsDataMockService';
import { getAlarm } from '../services/alarmApiService';
import { getArtifact } from '../services/sopFactoryService';
import { alarmApiRecordToAlarm } from '../services/alarmMappers';
import { ALARM_API_SITE_ID } from '../utils/constants';

/**
 * Resolve a single alarm for the detail page from the live AEM API, then resolve
 * its linked SOP (first `sop_ids` entry) from the SOP Factory and fold the SOP's
 * procedure steps into the returned `Alarm`. A missing/unlinked SOP degrades
 * gracefully to an alarm with no procedure steps.
 */
async function loadAlarmWithSop(alarmCode: string): Promise<Alarm | null> {
  const record = await getAlarm(ALARM_API_SITE_ID, alarmCode);
  if (!record) return null;

  const sopId = record.sop_ids?.[0];
  let sop = null;
  if (sopId) {
    try {
      sop = await getArtifact(sopId);
    } catch {
      // The linked SOP couldn't be resolved — show the alarm without procedure.
      sop = null;
    }
  }
  return alarmApiRecordToAlarm(record, sop);
}

interface AsyncResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function useAsync<T>(loader: () => Promise<T>, initial: T, deps: unknown[]): AsyncResult<T> {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await loader();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // loader identity intentionally excluded; deps describe the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, fetchKey]);

  return { data, loading, error, refetch };
}

export function useAlarms(query: AlarmQuery = {}) {
  const { siteId, bucket, group, sort } = query;
  const result = useAsync<Alarm[]>(
    () => alarmsService.getAlarms({ siteId, bucket, group, sort }),
    [],
    [siteId, bucket, group, sort]
  );
  return { alarms: result.data, ...result };
}

export function useAlarm(id: string | undefined) {
  const result = useAsync<Alarm | null>(
    () => (id ? loadAlarmWithSop(id) : Promise.resolve(null)),
    null,
    [id]
  );
  return { alarm: result.data, ...result };
}

export function useBucketCounts(siteId?: string) {
  const result = useAsync<Record<AlarmBucket, number>>(
    () => alarmsService.getBucketCounts(siteId),
    { critical: 0, high: 0, events: 0, closed: 0 },
    [siteId]
  );
  return { counts: result.data, ...result };
}

export function useAlarmGroups(siteId?: string) {
  const result = useAsync<string[]>(() => alarmsService.getAlarmGroups(siteId), [], [siteId]);
  return { groups: result.data, ...result };
}

export function useRoutines() {
  const result = useAsync<Routine[]>(() => opsDataService.getRoutines(), [], []);
  return { routines: result.data, ...result };
}

export function useMaintenanceTasks() {
  const result = useAsync<MaintenanceTask[]>(() => opsDataService.getMaintenanceTasks(), [], []);
  return { tasks: result.data, ...result };
}

export function useAudit(params: opsDataService.AuditQuery = {}) {
  const { query, actor, sort } = params;
  const result = useAsync<AuditEntry[]>(
    () => opsDataService.getAuditEntries({ query, actor, sort }),
    [],
    [query, actor, sort]
  );
  return { entries: result.data, ...result };
}

export function useLog(query?: string) {
  const result = useAsync<LogNote[]>(() => opsDataService.getLogNotes(query), [], [query]);
  return { notes: result.data, ...result };
}

export function useFloorPlan(siteId: string) {
  const result = useAsync<FloorPlan | null>(() => opsDataService.getFloorPlan(siteId), null, [
    siteId,
  ]);
  return { floorPlan: result.data, ...result };
}
